import React, { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { FormControlLabelAndPopover } from "../FormControlLabel";
import { ValidationErrorsMessages } from "../FormValidation";
import { CultureHelper } from "./../../helpers/CultureHelper";
import { getResourceString } from "./../../../kentico/resource-strings";

type CompanySearcherProps = {
  field: Field;
  handleChange: (target: CompanyDetailsType) => void;
  handleBlur: () => void;
  formDataEntry: FormDataEntry;
  city: string | undefined;
  errors: {
    message: string, 
    html: any
  }[];
};

export const CompanySearcherControl = ({ field, city, handleChange, handleBlur, formDataEntry, errors }: CompanySearcherProps) => {
  const [locale, setLocale] = useState(null);
  const [input, setInput] = useState('');
  const [value, setValue] = useState(formDataEntry?.value ? { Bedrijfsnaam: formDataEntry?.value, isNew: true} : null);

  const selectRef = useRef(null);
  
  useEffect(() => {
    const fetchCulture = async () => {
      const currentCulture = await CultureHelper.getCurrentCulture();
      setLocale(currentCulture.cultureImport);
    };

    fetchCulture();
  }, []);

  // State mismatch occurs when a city is changed while a company is selected
  useEffect(() => {
    if(value?.Bedrijfsnaam && formDataEntry.value !== value.Bedrijfsnaam) {
      setValue({...value, Bedrijfsnaam: formDataEntry.value});
    }
  }, [formDataEntry])

  const handleDropDownChange = (selectedOption) => {
    selectedOption.fieldName = field["@column"];
    setValue(selectedOption);
    handleChange(selectedOption);
  
    setInput('');
  };

  const handleInputChange = (newInputValue, { action }) => {
    if (action === 'input-change') {
      if(value?.isNew ?? false) {
        let option = {...value};
        option.Bedrijfsnaam = newInputValue;
        handleDropDownChange(option);
      }
      setInput(newInputValue);
    }

    if(action === 'menu-close' && newInputValue.length > 0) {
      setInput(newInputValue);
    }
  };

  const handleOnFocus = (): void => {
    setInput(value?.Bedrijfsnaam ?? input)
  }

  const handleOnBlur = (): void => {
    if(value?.Bedrijfsnaam) {
      setInput('')
    }
  }

  const returnOptionsWithDefault = (response) => {
    if(response && response.length > 0) {
      for (let index = 0; index < response.length; index++) {
        const element = response[index];
        element.isNew = false;
      }
    }

    if(value?.isNew ?? false) {
      return response;
    }
    else {
      return [...response, {
        Bedrijfsnaam: input,
        title: getResourceString("Form.Bedrijvenzoeker.NietGevonden.Label"),
        text: getResourceString("Form.Bedrijvenzoeker.NietGevonden.Tekst"),
        isNew: true
      }];
    }
  };

  const getItems = async (input: string) => {
    let url = `/api/bedrijvenzoeker/search?search=${input}&Plaats=${city}`;
    const response = await fetch(`${window.location.origin}/${url}`);
    const data = await response.json();

    return returnOptionsWithDefault(data);
  };

  const OptionTemplate = (props) => {
    const {value, innerRef, innerProps} = props;
    
    if (value.isNew) {
      return <div ref={innerRef} {...innerProps} className="option__bedrijfzoeker-bedrijf option__bedrijfzoeker-nieuw">
        <strong>{value.title}</strong>
        <p>{value.text}</p>
      </div>;
    }

    return <div ref={innerRef} {...innerProps} className="option__bedrijfzoeker-bedrijf">
      <strong>{value.Bedrijfsnaam}</strong>
      <p>{value.Straat} {value.AddressLine1}{value.AddressLine2}</p>
      <p>{value.PostalCode} {city}</p>
    </div>;
  };

  //Check for disabled on company dropdown
  const isDisabled = city === "" || city === null ? true : false;

  const handleOnKeyDown = (event): void => {
    if(event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();

      let option = {...value};

      if(!value || input != value.Bedrijfsnaam) {
        option = {
          Bedrijfsnaam: input,
          isNew: true
        };
      }

      handleDropDownChange(option);
      selectRef.current.blur();
    }
  }
  
  return (
    <div className={`form-group form__section-input ${field.properties?.fieldcssclass ?? ''}`}>
      <FormControlLabelAndPopover field={field} />
      {locale && (
        <AsyncSelect
          id={field["@column"]}
          name={field["@column"]}
          ref={selectRef}
          className="form-control searchahead-react-select"
          classNamePrefix="searchahead-react-select"
          value={value?.Bedrijfsnaam ?? ''}
          inputValue={input}
          placeholder={value?.Bedrijfsnaam ?? ''}
          onFocus={handleOnFocus}
          onKeyDown={handleOnKeyDown}
          onChange={handleDropDownChange}
          onInputChange={handleInputChange}
          onBlur={handleOnBlur}
          loadOptions={getItems}
          getOptionLabel={(e) => e.Bedrijfsnaam}
          getOptionValue={(e) => e}
          required={!field["@allowempty"]}
          isDisabled={isDisabled}
          components={{Option: OptionTemplate}}
          noOptionsMessage={() => null}
          loadingMessage={() => null}
          locale={locale}
        />
      )}
      <ValidationErrorsMessages keyGuid={field["@guid"]} errors={errors} />
    </div>
  );
};